<template>
    <div class="container">
      <div class="header">
        <h2 class="title">Teacher Subjects Assignment</h2>
        <p class="subtitle">View and manage teacher-subject assignments</p>
      </div>
  
      <div class="controls">
        <div class="search-filter">
          <input
            v-model="searchQuery"
            placeholder="Search teachers..."
            class="form-input"
          />
          <select v-model="subjectFilter" class="form-input">
            <option value="">All Subjects</option>
            <option v-for="subject in subjects" :key="subject.id" :value="subject.id">
              {{ subject.name }}
            </option>
          </select>
          <select v-model="classFilter" class="form-input">
            <option value="">All Classes</option>
            <option v-for="classItem in classes" :key="classItem.id" :value="classItem.id">
              {{ classItem.name }}
            </option>
          </select>
        </div>
      </div>
  
      <div class="table-container">
        <table class="modern-table">
          <thead>
            <tr>
              <th>Teacher</th>
              <th>Subjects</th>
              <th>Class</th>
              <th>Section</th>
              <th>Year</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="filteredAssignments.length > 0">
              <tr v-for="teacher in groupedTeachers" :key="teacher.id">
                <td class="teacher-cell">
                  <div class="teacher-info">
                    <span class="teacher-name">{{ teacher.name }}</span>
                    <span class="teacher-type">{{ teacher.type }}</span>
                  </div>
                </td>
                <td>
                  <div class="subject-list">
                    <div v-for="assignment in teacher.assignments" :key="assignment.id" class="subject-item">
                      <span class="subject-name">{{ assignment.subject.name }}</span>
                      <span class="subject-code">{{ assignment.subject.code }}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <div class="class-info">
                    <template v-if="teacher.assignments.length > 0">
                      {{ teacher.assignments[0].classroom.name }}
                    </template>
                  </div>
                </td>
                <td>
                  <div class="section-info">
                    <template v-if="teacher.assignments.length > 0">
                      {{ teacher.assignments[0].section.name }}
                    </template>
                  </div>
                </td>
                <td>
                  <div class="year-info">
                    <template v-if="teacher.assignments.length > 0">
                      {{ teacher.assignments[0].study_year.year_study }}
                    </template>
                  </div>
                </td>
                <td class="actions-cell">
                  <button @click="viewAssignments(teacher)" class="icon-button view-button">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  </button>
                  <button @click="editAssignments(teacher)" class="icon-button edit-button">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                </td>
              </tr>
            </template>
            <template v-else>
              <tr>
                <td colspan="6" class="no-results">
                  No assignments found matching your criteria
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
  
      <!-- Assignment Detail Modal -->
      <div v-if="selectedTeacher" class="modal-overlay" @click.self="closeModal">
        <div class="modal-content">
          <div class="modal-header">
            <h3>{{ selectedTeacher.name }}'s Assignments</h3>
            <button @click="closeModal" class="close-button">&times;</button>
          </div>
          <div class="modal-body">
            <div class="assignment-details">
              <div v-for="assignment in selectedTeacher.assignments" :key="assignment.id" class="assignment-item">
                <div class="assignment-info">
                  <span class="subject">{{ assignment.subject.name }} ({{ assignment.subject.code }})</span>
                  <span class="class-section">{{ assignment.classroom.name }} / {{ assignment.section.name }}</span>
                  <span class="year">{{ assignment.study_year.year_study }}</span>
                </div>
                <button @click="deleteAssignment(assignment.id)" class="icon-button delete-button">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="closeModal" class="action-button">Close</button>
          </div>
        </div>
      </div>



      <!-- Edit Assignment Modal -->
<!-- Edit Assignment Modal -->
<div v-if="editingAssignment" class="modal-overlay" @click.self="closeEditModal">
    <div class="modal-content w-full max-w-xl">
      <div class="modal-header">
        <h3>Edit Assignment</h3>
        <button @click="closeEditModal" class="close-button">&times;</button>
      </div>
      <div class="modal-body">
        <!-- Multi-select Subjects -->
        <div class="search-select" v-click-outside="() => showEditSubjectDropdown = false">
          <input
            v-model="editSubjectSearch"
            @focus="showEditSubjectDropdown = true"
            placeholder="Search subjects..."
            class="form-input"
          />
          <div v-if="showEditSubjectDropdown" class="dropdown">
            <div 
              v-for="s in filteredEditSubjects" 
              :key="s.id" 
              @mousedown.prevent="toggleEditSubject(s)"
              class="dropdown-item"
              :class="{ 'selected': isEditSubjectSelected(s.id) }"
            >
              {{ s.name }}
              <span v-if="isEditSubjectSelected(s.id)" class="check-mark">✓</span>
            </div>
          </div>
          <div class="selected-items-container">
            <div v-for="s in selectedEditSubjects" :key="s.id" class="selected-item">
              {{ s.name }}
              <span @click="removeEditSubject(s.id)" class="clear-btn">×</span>
            </div>
          </div>
        </div>

        <!-- Class Select -->
        <select v-model="editingAssignment.class_id" class="form-input">
          <option disabled value="">Select Class</option>
          <option v-for="c in classes" :key="c.id" :value="c.id">
            {{ c.name }}
          </option>
        </select>

        <!-- Section Select -->
        <select v-model="editingAssignment.section_id" class="form-input">
          <option disabled value="">Select Section</option>
          <option v-for="s in editSections" :key="s.id" :value="s.id">
            {{ s.name }}
          </option>
        </select>

        <!-- Study Year Select -->
        <select v-model="editingAssignment.study_year_id" class="form-input">
          <option disabled value="">Select Study Year</option>
          <option v-for="year in studyYears" :key="year.id" :value="year.id">
            {{ year.year_study }}
          </option>
        </select>

        <div class="modal-footer">
          <button @click="submitEditAssignment" class="action-button">Update Assignment</button>
        </div>
    </div>
  </div>
</div>


    </div>
  </template>
  
  <script>
  import axios from 'axios';
  
  const api = axios.create({
    baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api',
  });
  
  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
  
  export default {
    data() {
      return {
        assignments: [],
        teachers: [],
        subjects: [],
        classes: [],
        teacherTypes: [],
        studyYears: [], // Add this to store study years
        searchQuery: '',
        subjectFilter: '',
        classFilter: '',
        selectedTeacher: null,
        isLoading: false,
        editModalVisible: false,
        editingAssignment: null,
        editSections: [],
selectedEditSubjects: [],
editSubjectSearch: '',
showEditSubjectDropdown: false,
        editForm: {
  id: null,
  teacher_id: '',
  subject_id: '',
  class_id: '',
  section_id: '',
  study_year_id: ''
}
      };
    },
    computed: {
        filteredEditSubjects() {
  return this.subjects.filter(
    s => s.name.toLowerCase().includes(this.editSubjectSearch.toLowerCase()) &&
         !this.selectedEditSubjects.some(sel => sel.id === s.id)
  );
},

      filteredAssignments() {
        return this.assignments.filter(assignment => {
          const matchesSearch = assignment.teacher.name.toLowerCase().includes(this.searchQuery.toLowerCase());
          const matchesSubject = this.subjectFilter ? assignment.subject.id == this.subjectFilter : true;
          const matchesClass = this.classFilter ? assignment.class_id == this.classFilter : true;
          return matchesSearch && matchesSubject && matchesClass;
        });
      },
      groupedTeachers() {
        const grouped = {};
        
        this.filteredAssignments.forEach(assignment => {
          if (!grouped[assignment.teacher_id]) {
            grouped[assignment.teacher_id] = {
              id: assignment.teacher_id,
              name: assignment.teacher.name,
              type: assignment.teacher.type?.type || 'N/A',
              assignments: []
            };
          }
          grouped[assignment.teacher_id].assignments.push(assignment);
        });
        
        return Object.values(grouped);
      }
    },
    async mounted() {
      await this.fetchData();
      await this.fetchStudyYears(); // Fetch study years when component mounts
    },
    methods: {
        async fetchStudyYears() {
      try {
        const response = await api.get('/study-years');
        this.studyYears = response.data.data;
      } catch (error) {
        console.error('Error fetching study years:', error);
        this.$toast.error('Failed to load study years');
      }
    },
        async fetchData() {
  this.isLoading = true;
  try {
    const [assignmentsRes, subjectsRes, classesRes, typesRes] = await Promise.all([
      api.get('/teacher-subjects?include=teacher,subject,classroom,section,studyYear'),
      api.get('/subjects'),
      api.get('/classes'),
      api.get('/teacher-types')
    ]);

    const rawAssignments = assignmentsRes.data.data;
    this.assignments = Object.values(rawAssignments).flat();

    this.subjects = subjectsRes.data.data;
    this.classes = classesRes.data;
    this.teacherTypes = typesRes.data.data;
  } catch (error) {
    console.error('Error fetching data:', error);
    this.$toast.error('Failed to load data');
  } finally {
    this.isLoading = false;
  }
},
editAssignments(teacher) {
  const assignment = teacher.assignments[0]; // or open a modal to choose which one

  this.editingAssignment = {
    id: assignment.id,
    teacher_id: assignment.teacher.id,
    class_id: assignment.class_id,
    section_id: assignment.section_id,
   study_year_id: assignment.study_year?.id || ''
  };

  this.selectedEditSubjects = teacher.assignments.map(a => a.subject);
  this.fetchEditSections(assignment.class_id);
},
closeEditModal() {
  this.editingAssignment = null;
  this.selectedEditSubjects = [];
  this.editSubjectSearch = '';
  this.editSections = [];
},

toggleEditSubject(subject) {
  if (this.isEditSubjectSelected(subject.id)) {
    this.removeEditSubject(subject.id);
  } else {
    this.selectedEditSubjects.push(subject);
  }
  this.editSubjectSearch = '';
  this.showEditSubjectDropdown = false;
},

isEditSubjectSelected(subjectId) {
  return this.selectedEditSubjects.some(s => s.id === subjectId);
},



removeEditSubject(subjectId) {
  this.selectedEditSubjects = this.selectedEditSubjects.filter(s => s.id !== subjectId);
},


async fetchEditSections(classId) {
  if (!classId) return;
  try {
    const res = await api.get(`/classes/${classId}/sections`);
    this.editSections = res.data.sections || res.data.data;
  } catch (err) {
    console.error('Failed to load sections', err);
  }
},

async submitEditAssignment() {
  if (
    !this.editingAssignment.teacher_id ||
    !this.selectedEditSubjects.length ||
    !this.editingAssignment.class_id ||
    !this.editingAssignment.section_id ||
    !this.editingAssignment.study_year_id
  ) {
    this.$toast.error('Please fill all fields');
    return;
  }

  try {
    const payload = {
      teacher_id: this.editingAssignment.teacher_id,
      subject_ids: this.selectedEditSubjects.map(s => s.id),
      class_id: this.editingAssignment.class_id,
      section_id: this.editingAssignment.section_id,
      study_year_id: this.editingAssignment.study_year_id
    };

    await api.post('/teacher-subjects/bulk-store', payload);
    this.$toast.success('Assignment updated');
    this.closeEditModal();
    this.fetchAll();
  } catch (err) {
    console.error('Update failed:', err);
    this.$toast.error(err?.response?.data?.message || 'Update failed');
  }
},

async submitEdit() {
  try {
    const { id, ...payload } = this.editForm;
    const response = await api.put(`/teacher-subjects/${id}`, payload);
    this.$toast.success('Assignment updated successfully');

    // Refresh or update the assignment list locally
    const index = this.assignments.findIndex(a => a.id === id);
    if (index !== -1) {
      const updatedAssignment = { ...this.assignments[index], ...response.data.data };
      this.assignments[index] = updatedAssignment;
    }

    this.closeEditModal();
  } catch (error) {
    console.error('Failed to update assignment:', error);
    this.$toast.error('Failed to update assignment');
  }
},


      viewAssignments(teacher) {
        this.selectedTeacher = teacher;
      },

      async deleteAssignment(assignmentId) {
        if (!confirm('Are you sure you want to delete this assignment?')) return;
        
        try {
          await api.delete(`/teacher-subjects/${assignmentId}`);
          this.$toast.success('Assignment deleted successfully');
          this.assignments = this.assignments.filter(a => a.id !== assignmentId);
          this.closeModal();
        } catch (error) {
          console.error('Error deleting assignment:', error);
          this.$toast.error('Failed to delete assignment');
        }
      },
      closeModal() {
        this.selectedTeacher = null;
      }
    },
    watch: {
  'editingAssignment.class_id': {
    handler(newVal) {
      if (newVal) {
        this.fetchEditSections(newVal);
      } else {
        this.editSections = [];
      }
    }
  }
}

  };
  </script>
  
  <style scoped>
  .container {
    max-width: 1200px;
    margin: 2rem auto;
    padding: 1.5rem;
    background-color: #0a192f;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    color: #e6f1ff;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  }
  
  .header {
    margin-bottom: 2rem;
    text-align: center;
  }
  
  .title {
    font-size: 1.8rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #64ffda;
  }
  
  .subtitle {
    font-size: 0.9rem;
    color: #8892b0;
    margin: 0;
  }
  
  .controls {
    margin-bottom: 1.5rem;
  }
  
  .search-filter {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
  }
  
  .form-input {
    padding: 0.8rem 1rem;
    border: 1px solid #1e2a47;
    border-radius: 6px;
    background-color: #112240;
    color: #e6f1ff;
    font-size: 1rem;
    transition: all 0.3s ease;
    flex: 1;
    min-width: 200px;
  }
  
  .form-input:focus {
    outline: none;
    border-color: #64ffda;
    box-shadow: 0 0 0 2px rgba(100, 255, 218, 0.2);
  }
  
  .table-container {
    overflow-x: auto;
  }
  
  .modern-table {
    width: 100%;
    border-collapse: collapse;
    background-color: #112240;
    border-radius: 8px;
    overflow: hidden;
  }
  
  .modern-table th {
    background-color: #1e2a47;
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    color: #64ffda;
  }
  
  .modern-table td {
    padding: 1rem;
    border-bottom: 1px solid #1e2a47;
  }
  
  .modern-table tr:last-child td {
    border-bottom: none;
  }
  
  .modern-table tr:hover {
    background-color: rgba(100, 255, 218, 0.05);
  }
  
  .teacher-cell {
    min-width: 200px;
  }
  
  .teacher-info {
    display: flex;
    flex-direction: column;
  }
  
  .teacher-name {
    font-weight: 500;
  }
  
  .teacher-type {
    font-size: 0.8rem;
    color: #8892b0;
  }
  
  .subject-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .subject-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .subject-name {
    font-weight: 500;
  }
  
  .subject-code {
    font-size: 0.8rem;
    color: #8892b0;
    background: rgba(136, 146, 176, 0.1);
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
  }
  
  .actions-cell {
    display: flex;
    gap: 0.5rem;
  }
  
  .icon-button {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    background-color: transparent;
  }
  
  .icon-button:hover {
    transform: scale(1.1);
  }
  
  .view-button:hover {
    background-color: rgba(100, 255, 218, 0.1);
    color: #64ffda;
  }
  
  .edit-button:hover {
    background-color: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
  }
  
  .delete-button:hover {
    background-color: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }
  
  .no-results {
    text-align: center;
    padding: 2rem;
    color: #8892b0;
  }
  
  /* Modal Styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }
  
  .modal-content {
    background-color: #0a192f;
    border-radius: 8px;
    width: 90%;
    max-width: 600px;
    max-height: 80vh;
    overflow-y: auto;
    border: 1px solid #1e2a47;
  }
  
  .modal-header {
    padding: 1.5rem;
    border-bottom: 1px solid #1e2a47;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .modal-header h3 {
    margin: 0;
    color: #64ffda;
  }
  
  .close-button {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #8892b0;
  }
  
  .close-button:hover {
    color: #ef4444;
  }
  
  .modal-body {
    padding: 1.5rem;
  }
  
  .assignment-details {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  
  .assignment-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background-color: #112240;
    border-radius: 6px;
  }
  
  .assignment-info {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }
  
  .subject {
    font-weight: 500;
  }
  
  .class-section {
    font-size: 0.9rem;
    color: #8892b0;
  }
  
  .year {
    font-size: 0.8rem;
    background: rgba(100, 255, 218, 0.1);
    color: #64ffda;
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    display: inline-block;
  }
  
  .modal-footer {
    padding: 1.5rem;
    border-top: 1px solid #1e2a47;
    display: flex;
    justify-content: flex-end;
  }
  
  .action-button {
    padding: 0.8rem 1.5rem;
    background-color: #1e40af;
    color: white;
    border: none;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
  }
  
  .action-button:hover {
    background-color: #1e3a8a;
  }
  
  @media (max-width: 768px) {
    .search-filter {
      flex-direction: column;
    }
    
    .form-input {
      width: 100%;
    }
  }
  </style>